<template>
  <v-navigation-drawer
    v-model="drawer"
    data-elementName="MainNavigation"
    role="navigation"
    aria-label="Main navigation"
    :mini-variant="navState"
    app
    mini-variant-width="54"
    permanent
    :touchless="true"
    class="main-nav"
    width="250"
  >
    <div class="nav-layout">
      <div>
        <v-col class="logo-container pa-2">
          <v-btn
            aria-label="Navigation"
            icon
            @click.stop="toggleNav"
          >
            <v-icon>{{ drawerIcon }}</v-icon>
          </v-btn>

          <v-row>
            <v-col class="col-auto">
              <router-link
                to="/"
                class="logo"
              >
                <img
                  :src="navigationLogo"
                  style="margin-left: 12px; margin-top: 6px; height: 36px; width: auto; max-height: 36px;"
                  aria-label="adops.com"
                >
              </router-link>
            </v-col>

            <v-col
              v-if="!navState"
              class="col-auto pa-0 my-auto"
            >
              <span class="title font-weight-bold">
                {{ inDemoMode ? 'iAdOps Demo' : 'iAdOps' }}
              </span>
            </v-col>
          </v-row>
        </v-col>

        <v-divider />

        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <a
              href="javascript:;"
              :class="`logged-in-user-info cursor-pointer ${navState ? 'mx-auto' : 'ml-3'}`"
              v-on="on"
            >
              <v-avatar>
                <img
                  v-if="hasUserPhoto"
                  :src="user.photoURL"
                  :alt="avatarAltText"
                  referrerPolicy="no-referrer"
                  style="height: 33px; width: 33px; margin: 4px;"
                >
                <div
                  v-else
                  class="avatar-fallback"
                  :style="avatarFallbackStyle"
                  :aria-label="avatarAltText"
                >
                  {{ userInitials }}
                </div>
              </v-avatar>

              <div class="user-details">
                <div class="user-name">
                  {{ user.displayName }}
                </div>

                <div class="user-email">
                  {{ user.email }}
                </div>

                <div
                  v-if="inAliasMode"
                  class="user-email"
                >
                  <span class="user-email">
                    as
                  </span>
                  <span class="user-email">
                    {{ aliasEmail }}
                  </span>
                </div>

                <div
                  v-if="inDemoMode"
                  class="user-email font-weight-medium primary--text"
                >
                  Demo Mode On
                </div>
              </div>
            </a>
          </template>

          <v-list>
            <v-list-item
              v-if="hasPermissionForSettings"
              class="caption"
              @click="displaySettingsModal"
            >
              <v-list-item-title class="coolgray--text">
                <v-icon
                  class="pr-2 coolgray--text"
                  small
                >
                  format_paint
                </v-icon>
                <span>Settings</span>
              </v-list-item-title>
            </v-list-item>

            <v-list-item @click="signOut">
              <v-list-item-title class="toasted--text">
                <v-icon
                  class="pr-2 toasted--text"
                  small
                >
                  power_settings_new
                </v-icon>
                <span>Sign Out</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-divider />
      </div>

      <div class="navigation-scroll">
        <v-list :class="`pt-0 menu-items ${navState ? 'pl-0' : 'pl-1'}`">
          <div
            v-for="(nav, navIndex) in navigation"
            :key="navIndex"
          >
            <v-divider
              v-if="nav.divider === 'pre'"
            />

            <v-tooltip
              v-if="navState"
              right
            >
              <template v-slot:activator="{ on }">
                <v-list-item
                  link
                  tag="button"
                  type="button"
                  :aria-label="nav.title"
                  :class="`menu-item ${navState ? 'pl-4' : 'pl-4'}`"
                  active-class="router-active-link"
                  @click="selectSearchItem(nav)"
                  v-on="on"
                >
                  <v-list-item-action class="mr-0 justify-center">
                    <img
                      :src="nav.image"
                      :width="nav.imageWidth || imageWidth"
                    >
                  </v-list-item-action>

                  <v-list-item-content :class="navState ? 'ml-1' : 'ml-3'">
                    <v-list-item-title>
                      <span class="subtitle-2 font-weight-medium">{{ nav.title }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>

              {{ nav.tooltip }}
            </v-tooltip>

            <v-list-item
              v-else
              link
              tag="button"
              type="button"
              :aria-label="nav.title"
              :class="`menu-item ${navState ? 'pl-4' : 'pl-4'}`"
              active-class="router-active-link"
              @click="selectSearchItem(nav)"
            >
              <v-list-item-action class="mr-0 justify-center">
                <img
                  :src="nav.image"
                  :width="nav.imageWidth || imageWidth"
                >
              </v-list-item-action>

              <v-list-item-content :class="navState ? 'ml-1' : 'ml-3'">
                <v-list-item-title>
                  <span class="subtitle-2 font-weight-medium">{{ nav.title }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <div class="sub-items-container">
              <router-link
                v-for="subNav in nav.subItems"
                :key="subNav.title"
                class="sub-item-link"
                :to="subNav.link"
              >
                {{ subNav.title }}
              </router-link>
            </div>

            <v-divider
              v-if="nav.divider === 'post'"
            />
          </div>
        </v-list>
      </div>

      <div
        v-if="drawer"
        :class="`nav-footer ${navFooterThemeClass}`"
      >
        <v-divider class="mx-0 mb-3" />

        <div :class="`nav-utility-links ${navState ? 'justify-center' : ''}`">
          <v-btn
            v-for="link in utilityLinks"
            :key="link.to"
            text
            small
            class="text-none nav-utility-btn"
            :to="link.to"
          >
            <font-awesome-icon
              :icon="link.icon"
              size="sm"
              :class="navState ? '' : 'mr-2'"
            />
            <span v-if="!navState">{{ link.label }}</span>
          </v-btn>
        </div>

        <div
          v-if="!navState"
          class="links-container pl-6 pb-3"
        >
          <div>
            <span class="copywrite">&copy; {{ new Date().getFullYear() }}, adops.com</span>
          </div>

          <div class="caption">
            {{ version }}
          </div>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import layoutActionNames from '../store/actionNames'
import themeClasses from '@/mixins/themeClasses'
import {
  fallbackToDashboard,
  findSelectedSearch,
  selectSearchItem,
} from '../utils/navigationSelection'

const {
  mapGetters: mapAuthGetters,
  mapActions: mapAuthActions,
  mapState: mapAuthState,
} = createNamespacedHelpers('auth')
const { mapState, mapMutations } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Navigation',
  mixins: [themeClasses],
  data() {
    return {
      drawer: true,
      imageWidth: '18px',
    }
  },
  computed: {
    ...mapAuthState({
      demoMode: (state) => state.demoMode,
      user: (state) => state.user,
    }),
    ...mapAuthGetters([
      'aliasEmail',
      'isActiveUser',
      'inAliasMode',
      'inDemoMode',
      'permissions',
    ]),
    ...mapToolsGetters([
      'getReportKey',
    ]),
    ...mapToolsState({
      navigation: (state) => state.navigation,
      navigationOptions: (state) => state.navigationOptions,
      searchResults: (state) => state.searchResults,
      selectedSearch: (state) => state.selectedSearch,
    }),
    ...mapState({
      icons: (state) => state.icons,
      isSettingsModalVisible: (state) => state.isSettingsModalVisible,
      navCollapsed: (state) => state.navCollapsed,
      version: (state) => state.version,
    }),
    showSettingsModal: {
      get() {
        return this.isSettingsModalVisible
      },
      set(value) {
        this.$store.commit('layout/isSettingsModalVisible', value, { root: true })
      },
    },
    navState: {
      get() {
        return this.navCollapsed
      },
      set() {
        this.$store.commit('layout/navCollapsed')
      },
    },
    drawerIcon() {
      return this.navCollapsed ? 'chevron_right' : 'chevron_left'
    },
    navigationLogo() {
      const isDark = this.isDarkThemeContext()
      return isDark ? this.icons.navigation.logoDarkMode : this.icons.navigation.logoLightMode
    },
    navFooterThemeClass() {
      const surface = this.themeSurfaceColor('icewhite', 'vuetifyblack')
      const text = this.themeTextClass('black--text', 'white--text')
      return `${surface} ${text}`
    },
    hasPermissionForSettings() {
      const { isActiveUser } = this

      return isActiveUser
    },
    hasUserPhoto() {
      const photoURL = this.user && this.user.photoURL
      return typeof photoURL === 'string' && photoURL.length > 0
    },
    userDisplayName() {
      return (this.user && this.user.displayName) || (this.user && this.user.email) || 'User'
    },
    userInitials() {
      const source = this.userDisplayName.trim()

      if (!source.length) return 'U'

      const parts = source.split(/\s+/).filter(Boolean)

      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
      }

      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    },
    avatarAltText() {
      return `${this.userDisplayName} avatar`
    },
    avatarFallbackStyle() {
      const seed = (this.user && this.user.email) || this.userDisplayName
      let hash = 0

      for (let i = 0; i < seed.length; i += 1) {
        hash = (hash * 31 + seed.charCodeAt(i)) % 100000
      }

      const hue = Math.abs(hash) % 360

      return {
        alignItems: 'center',
        backgroundColor: `hsl(${hue}, 65%, 44%)`,
        borderRadius: '50%',
        color: '#fff',
        display: 'flex',
        fontSize: '11px',
        fontWeight: '700',
        height: '33px',
        justifyContent: 'center',
        letterSpacing: '0.4px',
        margin: '4px',
        textTransform: 'uppercase',
        width: '33px',
      }
    },
    utilityLinks() {
      return [
        {
          label: 'What\'s New',
          to: '/docs/release-summary',
          icon: ['fas', 'file-alt'],
        },
      ]
    },
  },
  // Navigation is initialized in auth flow after successful sign-in
  // No need for mounted() hook - navigation is guaranteed to be ready
  // when this component renders (user must be signed in to see navigation)
  methods: {
    ...mapAuthActions(['signOut']),
    ...mapMutations({
      toggleNav: layoutActionNames.TOGGLE_NAV,
    }),
    displaySettingsModal() {
      this.showSettingsModal = true
    },
    fallbackToDashboard({ reportId, reportKey, searchEndpoint }) {
      return fallbackToDashboard({ reportId, reportKey, searchEndpoint })
    },
    async findSelectedSearch(nav) {
      return findSelectedSearch({
        dispatch: (...args) => this.$store.dispatch(...args),
        navigationOptions: this.navigationOptions,
        nav,
        searchResults: this.searchResults,
      })
    },
    async selectSearchItem(nav) {
      return selectSearchItem({
        $route: this.$route,
        commit: (...args) => this.$store.commit(...args),
        dispatch: (...args) => this.$store.dispatch(...args),
        nav,
        searchResults: this.searchResults,
      })
    },
    navigateTo(path) {
      if (!path) return
      this.$router.push(path).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.main-nav {
  display: flex;
  flex-direction: column;
}

.main-nav ::v-deep .v-navigation-drawer__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.nav-layout {
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.navigation-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 8px;
}

.v-divider {
  border-color: $color-nav-divider-glow;
}

.logo-container {
  display: flex;
  align-items: center;
  padding: 15px;

  button {
    margin: 0;
  }

  img {
    height: 36px !important;
    width: auto !important;
    max-height: 36px !important;
    max-width: 100% !important;
  }
}

.logged-in-user-info {
  display: flex;
  align-items: center;

  .avatar-fallback {
    line-height: 1;
  }

  ::v-deep.v-avatar {
    height: 30px !important;
    min-width: auto !important;
    width: 30px !important;

    transition-duration: 0.2s;
    transition-property: height, width, transform;

    box-shadow: 0 2px 6px 3px rgba(165, 177, 185, 0.3);
  }

  .user-details {
    padding: 15px 0 15px 15px;
    overflow: hidden;

    .user-name {
      color: $color-monochrome-darkestblue;
      font-size: $size-regular;
      font-weight: bold;
      letter-spacing: 0.43px;
      line-height: 21px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .user-email {
      color: $color-monochrome-gray;
      font-size: $size-small;
      font-weight: 500;
      letter-spacing: 0.36px;
      line-height: 18px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.menu-items {
  .menu-item {
    color: $color-monochrome-darkestblue;
    width: 100%;
    text-align: left;

    ::v-deep.v-list__tile {
      height: inherit;
    }

    &:focus-visible {
      outline: 3px solid #005fcc;
      outline-offset: -2px;
      border-radius: 6px;
    }
  }
}

.logged-in-user-info:focus-visible,
.sub-item-link:focus-visible,
.nav-utility-btn:focus-visible,
.logo:focus-visible {
  outline: 3px solid #005fcc;
  outline-offset: 2px;
  border-radius: 6px;
}

.sub-items-container {
  display: flex;
  flex-direction: column;
  margin-left: 75px;

  .sub-item-link {
    padding-top: 9px;
    color: $color-monochrome-gray;
    font-size: $size-regular;
    letter-spacing: 0.36px;
    line-height: 18px;
    &:hover {
      color: #00aed6;
    }

    &.router-link-active {
      font-weight: bolder;
    }
  }
}

::v-deep.router-active-link {
  background-color: $color-offwhite;
  color: inherit;
}

.nav-footer {
  flex-shrink: 0;
  margin-top: auto;

  .nav-utility-links {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 0 16px 12px;
  }

  .nav-utility-btn {
    min-width: 0;
  }

  .help-icon .v-icon {
    font-size: 33px;
    color: $color-monochrome-darkestblue;
  }

  .links-container {
    flex: 1;
    white-space: nowrap;

    .copywrite {
      font-size: $size-small;
      font-weight: 500;
      letter-spacing: 0.36px;
      line-height: 18px;
    }
  }

  .footer-links {
    display: flex;
    justify-content: flex-start;
    padding-right: 48px;

    color: $color-monochrome-gray;
    font-size: $size-small;
    font-weight: 500;
    letter-spacing: 0.36px;
    line-height: 20px;

    a {
      color: inherit;
    }

    .bullet-divider {
      margin: 0 1ch;
    }
  }
}

.v-navigation-drawer {
  box-shadow: 0 2px 6px 0 rgba(0, 148, 255, 0.21);
}

.v-navigation-drawer--mini-variant {
  box-shadow: none;

  .logo-container {
    justify-content: start;

    img {
      display: none;
    }
  }

  .logged-in-user-info {
    padding: 9px 0;

    ::v-deep.v-avatar {
      height: 30px !important;
      width: 30px !important;

      transform: translateX(4px);
    }

    .user-details {
      display: none;
    }
  }

  .sub-items-container {
    display: none;
  }

  .nav-footer {
    .nav-utility-links {
      padding: 0 0 12px;
    }

    .links-container {
      display: none;
    }
  }
}
</style>
